import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

const CIRCUMFERENCE = 2 * Math.PI * 47
const MIME_CANDIDATES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"]

// Enregistre la question orale (MediaRecorder, 60 s max) puis l'envoie en pièce jointe Active Storage.
export default class extends Controller {
  static targets = ["button", "micIcon", "stopIcon", "hint", "timer", "progress"]
  static values = { maxSeconds: { type: Number, default: 60 }, url: String }

  disconnect() {
    this.cleanup()
    clearTimeout(this.fallbackTimer)
  }

  toggle() {
    if (this.recorder?.state === "recording") this.stop()
    else this.start()
  }

  async start() {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      return this.fallback("Votre navigateur ne permet pas d'enregistrer. Écrivez votre question à la place.")
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      return this.fallback("Le micro n'est pas accessible. Autorisez-le dans votre navigateur ou écrivez votre question.")
    }

    const mimeType = MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type))
    this.recorder = new MediaRecorder(this.stream, mimeType ? { mimeType } : undefined)
    this.chunks = []
    this.recorder.addEventListener("dataavailable", (event) => { if (event.data.size) this.chunks.push(event.data) })
    this.recorder.addEventListener("stop", () => this.upload())
    this.recorder.start()
    this.watchLevel()

    this.startedAt = Date.now()
    this.setRecording(true)
    this.tick()
    this.interval = setInterval(() => this.tick(), 250)
  }

  stop() {
    if (this.recorder?.state === "recording") this.recorder.stop()
  }

  tick() {
    const elapsed = Math.min(Math.floor((Date.now() - this.startedAt) / 1000), this.maxSecondsValue)
    // Compte à rebours discret et anneau de progression autour du bouton.
    this.timerTarget.textContent = this.format(this.maxSecondsValue - elapsed)
    this.progressTarget.style.strokeDashoffset = String(CIRCUMFERENCE * (1 - (Date.now() - this.startedAt) / 1000 / this.maxSecondsValue))
    if (elapsed >= this.maxSecondsValue) this.stop()
  }

  async upload() {
    const type = this.recorder.mimeType || "audio/webm"
    const blob = new Blob(this.chunks, { type })
    this.cleanup()
    this.setRecording(false)
    this.hintTarget.textContent = "Envoi de votre question…"
    this.buttonTarget.disabled = true

    const extension = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm"
    const body = new FormData()
    body.append("question[audio]", blob, `question.${extension}`)

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        body,
        headers: {
          "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content,
          Accept: "application/json"
        }
      })
      if (!response.ok) throw new Error(response.statusText)
      const { url } = await response.json()
      Turbo.visit(url)
    } catch {
      this.hintTarget.textContent = "L'envoi n'a pas abouti. Réessayez ou écrivez votre question."
      this.buttonTarget.disabled = false
    }
  }

  // Niveau sonore du micro (Web Audio, AnalyserNode) lissé et publié dans --niveau (0 à 1) : les ondes l'amplifient.
  watchLevel() {
    try {
      const Context = window.AudioContext || window.webkitAudioContext
      this.audio = new Context()
      const analyser = this.audio.createAnalyser()
      analyser.fftSize = 256
      this.audio.createMediaStreamSource(this.stream).connect(analyser)
      const samples = new Uint8Array(analyser.fftSize)
      let level = 0
      const loop = () => {
        analyser.getByteTimeDomainData(samples)
        let sum = 0
        for (const v of samples) sum += ((v - 128) / 128) ** 2
        const target = Math.min(1, Math.sqrt(sum / samples.length) * 4)
        level += (target - level) * 0.2
        this.element.style.setProperty("--niveau", level.toFixed(3))
        this.raf = requestAnimationFrame(loop)
      }
      loop()
    } catch { /* pas de mesure du niveau : les ondes tournent à vitesse fixe */ }
  }

  // Micro refusé ou indisponible : message sous le bouton, puis bascule sur l'onglet Écrire.
  fallback(message) {
    this.hintTarget.textContent = "Micro indisponible : l'onglet Écrire vous attend"
    this.hintTarget.title = message
    this.element.classList.add("is-error")
    clearTimeout(this.fallbackTimer)
    this.fallbackTimer = setTimeout(() => this.dispatch("unavailable"), 2500)
  }

  setRecording(recording) {
    this.element.classList.toggle("is-recording", recording)
    this.element.classList.remove("is-error")
    this.buttonTarget.setAttribute("aria-pressed", String(recording))
    if (!recording) this.progressTarget.style.strokeDashoffset = String(CIRCUMFERENCE)
    this.timerTarget.hidden = !recording
    this.micIconTarget.hidden = recording
    this.stopIconTarget.hidden = !recording
    this.hintTarget.textContent = ""
    this.hintTarget.removeAttribute("title")
    this.buttonTarget.setAttribute("aria-label", recording ? "Appuyer pour terminer l'enregistrement" : "Appuyer pour enregistrer votre question (60 secondes maximum)")
  }

  cleanup() {
    clearInterval(this.interval)
    cancelAnimationFrame(this.raf)
    this.audio?.close().catch(() => {})
    this.audio = null
    this.element.style.removeProperty("--niveau")
    this.stream?.getTracks().forEach((track) => track.stop())
    this.stream = null
  }

  format(seconds) {
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`
  }
}
